import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "../../api";
import {
  InvocationStatusEnum,
  type GenericPayload,
  type MyPayEnvironment,
} from "../../generated/core-api";
import {
  useCompanyProvisionAgentId,
  isTerminalInvocationStatus,
} from "../../onboarding";
import {
  clientSplitCostingValidationErrors,
  type SplitCostingReportState,
  type SplitCostingRow,
} from "../utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SplitCostingImportManagerReturn {
  /** Rows bound via `setPendingRows` ahead of `validate()` — sourced by the
   *  caller from `wizardCtx.parsedRows`. */
  rows: SplitCostingRow[];
  /** Company code bound via `setPendingRows`. Never read from the file (R-CTX). */
  companyCode: string | null;
  /** Datapay environment bound via `setPendingRows`. */
  mypayEnvironment: MyPayEnvironment | null;
  /**
   * File identifier (original file name) bound via `setPendingRows` — sent
   * as `file_identifier` in `commit()`'s payload for the API's audit record
   * (R-GOV-3). `null` if the wizard context had no file name at bind time.
   */
  fileIdentifier: string | null;
  /**
   * Set once the `split_costing_import` validate workflow reaches `COMPLETED`
   * with a result narrowed by `isSplitCostingImportResult`. `null` while
   * pending, on a non-COMPLETED terminal status, or if the payload doesn't
   * match the expected shape (see `managerError` for that last case).
   */
  validationReport: SplitCostingImportWorkflowResult | null;
  /**
   * Last completed validation report retained for presentation while edits
   * make the current revision stale. Errors for edited rows are removed from
   * this snapshot; commit guards must continue to use `validationReport`.
   */
  validationDisplayReport: SplitCostingImportWorkflowResult | null;
  /**
   * True once the bound rows change (re-upload) after a completed validation.
   * Flips in `setPendingRows` when the incoming rows differ from the
   * currently bound rows and a prior `validate()` had already reached
   * `COMPLETED`; clears again once a fresh `validate()` reaches `COMPLETED`.
   * Company-context staleness needs no explicit watch here — changing or
   * clearing the company unmounts `SplitCostingWizardProvider`, which
   * unmounts this hook too.
   */
  validationStale: boolean;
  /**
   * Surface-level error, in precedence order: an imperative guard failure
   * (missing context, agent not configured), an invoke-call failure
   * classified by status code (404 agent not found, 409 idempotency
   * conflict, 503 worker not enabled, network failure), the poll
   * attempt-cap timeout, a terminal poll-request failure, then a terminal
   * poll-status error (FAILED/TERMINATED surfacing the workflow's `error`
   * field, or an unexpected-shape COMPLETED payload).
   */
  managerError: string | null;
  isValidating: boolean;
  /**
   * True while the onboarding agent id / service-account id are still being
   * read from the environment. `validate()`'s "not available in this
   * environment" guard is only meaningful once this is false — a caller that
   * auto-triggers on mount must wait, or it will trip the guard against a
   * config that simply hasn't loaded yet.
   */
  isConfigLoading: boolean;
  hasRows: boolean;
  /**
   * True from the first accepted batch until a wholly successful commit or an
   * explicit clear. Used by replacement and navigation discard guards.
   */
  hasUncommittedImportProgress: boolean;
  /** Monotonic revision of the rows and context submitted for validation. */
  validationInputRevision: number;
  /** Revision captured by the latest validation submission. */
  submittedValidationRevision: number | null;
  /** Idempotency key minted by the most recent `validate()` call — reused by `commit()`. */
  idempotencyKey: string | null;
  /**
   * Set once the `split_costing_import` commit workflow reaches `COMPLETED`
   * with a result narrowed by `isSplitCostingImportResult`. `null` until a
   * `commit()` call has been submitted and its poll completes.
   */
  commitResult: SplitCostingImportWorkflowResult | null;
  isCommitting: boolean;
  /**
   * Bind the parsed rows and company context ahead of `validate()`.
   *
   * `fileIdentifier` (the original file name, `wizardCtx.fileName`) is bound
   * here rather than passed to `commit()` — like `companyCode` and
   * `mypayEnvironment`, it is wizard context captured once at upload time
   * (step 4), not per-call UI state (contrast `warningsAcknowledged`, which
   * `commit()` takes as a parameter because it lives in the validate
   * screen). Carried through to `commit()`'s payload for the API's audit
   * record (R-GOV-3); required on every invocation — `validate()` and
   * `commit()` refuse to run when it is missing.
   */
  setPendingRows: (
    rows: SplitCostingRow[],
    companyCode: string,
    mypayEnvironment: MyPayEnvironment,
    fileIdentifier?: string | null
  ) => void;
  /**
   * Replaces one row synchronously in both the ref and React state, invalidates
   * every prior validate/commit result, and marks the batch stale.
   */
  updateRow: (index: number, row: SplitCostingRow) => void;
  /**
   * Invokes the `split_costing_import` workflow with `mode: "validate"`.
   * Mints a fresh idempotency key, held in a ref and reused by `commit()`.
   */
  validate: () => Promise<void>;
  /**
   * Invokes the `split_costing_import` workflow with `mode: "commit"`,
   * reusing the idempotency key minted by the most recent `validate()` call.
   * The warning-acknowledgement state lives outside this hook (the validate
   * screen / `SplitCostingValidationReport`), so it is passed in rather than
   * tracked here.
   *
   * Refuses to run (no-op) when:
   * - a commit is already in flight (`isCommitting` is `true`)
   * - no idempotency key has been minted yet (`validate()` hasn't been called)
   * - `validationStale` is `true` (rows changed since the last completed validate)
   * - there is no completed `validationReport`, or it has any blocking error (`failed > 0`)
   * - `warningsAcknowledged` is `false`
   */
  commit: (warningsAcknowledged: boolean) => Promise<void>;
  clear: () => void;
}

// ---------------------------------------------------------------------------
// Contract row — the shape the API expects on invoke.
// Drops the portal-only `fileRow` field and coerces `apportionedPercentage`
// from string to number. `endDate` is sent on the wire so Pacific can run
// end-date validation (AC-ERR-07, AC-ERR-10) at validate time — validate and
// commit must send the same shape. The parse step normalises any value to
// `yyyy-MM-dd` (or null), so the API always receives a proper ISO date string.
// ---------------------------------------------------------------------------

export interface SplitCostingContractRow {
  employeeCode: string;
  apportionedPercentage: number;
  startDate: string | null;
  endDate: string | null;
  path: SplitCostingRow["path"];
}

function toContractRow(row: SplitCostingRow): SplitCostingContractRow {
  return {
    employeeCode: row.employeeCode,
    apportionedPercentage: Number(row.apportionedPercentage),
    startDate: row.startDate,
    endDate: row.endDate,
    path: row.path,
  };
}

// ---------------------------------------------------------------------------
// Type guard — narrows AgentInvocationStatus.result (typed unknown) to the
// split_costing_import workflow's own result shape (see
// `agentic-split-costing-api-contract.json` in Product-PRDs).
// ---------------------------------------------------------------------------

/**
 * One error entry in the workflow's result. `index` is the 0-based position
 * in the `rows` array sent on invoke (`null` for the R-PRQ-1 prerequisite
 * error, which blocks the whole batch rather than a single row).
 */
export interface SplitCostingImportError {
  index: number | null;
  employeeCode: string | null;
  /** Offending field name (e.g. `"apportionedPercentage"`, `"endDate"`); `null` when the error isn't field-specific. */
  field: string | null;
  error: string;
}

/**
 * One Pacific Datapay-aware advisory from the workflow result (R-VAL-3 /
 * AC-APP-07). Grouped by (employee, destination); `indices` are zero-based
 * positions in the invoke `rows` array.
 */
export interface SplitCostingImportApiWarning {
  code: string;
  employeeCode: string | null;
  indices: number[];
  warning: string;
}

export interface SplitCostingImportWorkflowResult {
  /** Rows read. */
  total: number;
  /** Always 0 in validate mode — the workflow forces this so a dry run can't read as a partial write. */
  succeeded: number;
  /** Count of unique error indices, not raw error count. */
  failed: number;
  errors: SplitCostingImportError[];
  /**
   * Pacific Datapay-aware advisories (R-VAL-3 / AC-APP-07) when the API flag
   * is on. Empty / absent when the flag is off — the portal then computes
   * AC-APP-04/AC-APP-07 client-side. Christmas wire warnings are never
   * included. Defensively parsed via {@link readApiWarnings}.
   */
  warnings?: SplitCostingImportApiWarning[];
}

/**
 * True when validation returned a coherent result for the complete submitted
 * batch. Batch-level prerequisite errors are coherent but still block commit.
 */
export function isSplitCostingValidationComplete(
  result: SplitCostingImportWorkflowResult | null,
  submittedRowCount: number
): result is SplitCostingImportWorkflowResult {
  if (
    result === null ||
    submittedRowCount <= 0 ||
    result.total !== submittedRowCount ||
    result.succeeded !== 0
  ) {
    return false;
  }
  const indexedErrors = result.errors
    .map((error) => error.index)
    .filter((index): index is number => index !== null);
  if (
    indexedErrors.some(
      (index) =>
        !Number.isInteger(index) || index < 0 || index >= submittedRowCount
    )
  ) {
    return false;
  }
  return result.failed === new Set(indexedErrors).size;
}

/** True only when complete validation found no blocking errors. */
export function isSplitCostingValidationCommitReady(
  result: SplitCostingImportWorkflowResult | null,
  submittedRowCount: number
): result is SplitCostingImportWorkflowResult {
  return (
    isSplitCostingValidationComplete(result, submittedRowCount) &&
    result.failed === 0 &&
    result.errors.length === 0
  );
}

/** True only when commit reports complete success for every submitted row. */
export function isSplitCostingCommitSuccessful(
  result: SplitCostingImportWorkflowResult | null,
  submittedRowCount: number
): result is SplitCostingImportWorkflowResult {
  return (
    result !== null &&
    submittedRowCount > 0 &&
    result.total === submittedRowCount &&
    result.succeeded === submittedRowCount &&
    result.failed === 0 &&
    result.errors.length === 0
  );
}

export interface ResolveSplitCostingReportStateInput {
  result: SplitCostingImportWorkflowResult;
  submittedRowCount: number;
  resultPhase: "validation" | "commit";
  isCommitting: boolean;
  hasPrerequisiteErrors: boolean;
  validationStale?: boolean;
  hasClientValidationErrors?: boolean;
}

/** Derives the truthful batch-level presentation state for the report. */
export function resolveSplitCostingReportState({
  result,
  submittedRowCount,
  resultPhase,
  isCommitting,
  hasPrerequisiteErrors,
  validationStale = false,
  hasClientValidationErrors = false,
}: ResolveSplitCostingReportStateInput): SplitCostingReportState {
  if (isCommitting) return "committing";
  if (resultPhase === "commit") {
    return isSplitCostingCommitSuccessful(result, submittedRowCount)
      ? "committed"
      : "inconsistent";
  }
  if (
    hasPrerequisiteErrors ||
    !isSplitCostingValidationComplete(result, submittedRowCount)
  ) {
    return "batchNotEvaluated";
  }
  if (validationStale || hasClientValidationErrors) return "localReady";
  return "validation";
}

export function isSplitCostingImportResult(
  value: unknown
): value is SplitCostingImportWorkflowResult {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.total === "number" &&
    typeof candidate.succeeded === "number" &&
    typeof candidate.failed === "number" &&
    Array.isArray(candidate.errors) &&
    (candidate.warnings === undefined || Array.isArray(candidate.warnings))
  );
}

/**
 * Defensively narrow `result.warnings` to structured API warnings.
 * Malformed entries are dropped so an older API build or partial payload
 * cannot break the validate screen.
 */
export function readApiWarnings(value: unknown): SplitCostingImportApiWarning[] {
  if (!Array.isArray(value)) return [];
  const warnings: SplitCostingImportApiWarning[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as Record<string, unknown>;
    const warning = typeof row.warning === "string" ? row.warning : null;
    const code = typeof row.code === "string" ? row.code : "AC-APP-07";
    const employeeCode =
      typeof row.employeeCode === "string"
        ? row.employeeCode
        : typeof row.employee_code === "string"
          ? row.employee_code
          : null;
    if (warning === null || !Array.isArray(row.indices)) continue;
    const indices = row.indices.filter((i): i is number => typeof i === "number");
    if (indices.length === 0) continue;
    warnings.push({ code, employeeCode, indices, warning });
  }
  return warnings;
}

function omitValidationErrorsForRow(
  result: SplitCostingImportWorkflowResult,
  rowIndex: number
): SplitCostingImportWorkflowResult {
  const errors = result.errors.filter((error) => error.index !== rowIndex);
  const failed = new Set(
    errors
      .map((error) => error.index)
      .filter((index): index is number => index !== null)
  ).size;
  return { ...result, failed, errors };
}

function mergeClientValidationErrors(
  result: SplitCostingImportWorkflowResult,
  rows: readonly SplitCostingRow[]
): SplitCostingImportWorkflowResult {
  const existingKeys = new Set(
    result.errors.map((error) => `${error.index}:${error.field}`)
  );
  const clientErrors = clientSplitCostingValidationErrors(rows).filter(
    (error) => !existingKeys.has(`${error.index}:${error.field}`)
  );
  if (clientErrors.length === 0) return result;

  const errors = [...result.errors, ...clientErrors];
  const indexedFailures = new Set(
    errors
      .map((error) => error.index)
      .filter((index): index is number => index !== null)
  ).size;
  return {
    ...result,
    failed: Math.max(result.failed, indexedFailures),
    errors,
  };
}

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

/**
 * Extract a human-readable message from an agent invocation failure
 * (thrown by `api.invokeAgent`, i.e. before a workflow ID exists to poll).
 * Mirrors `useWorkPatternImportInvocation`'s `classifyInvocationError`,
 * trimmed to the status codes this hook's V2 step is scoped to.
 */
function classifyInvocationError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;

    if (status === 0 || !error.response) {
      return "Could not reach the API server. Make sure the API is running and try again.";
    }
    if (status === 404) {
      return "Agent not found (404). Check that VITE_ONBOARDING_AGENT_ID is set to a valid agent ID in your .env file.";
    }
    if (status === 409) {
      return "This batch was already submitted. If validation completed, refresh the page. Otherwise, start a new import.";
    }
    if (status === 503) {
      return "Onboarding worker is not enabled (503). Set ONBOARDING_WORKER_ENABLED=true in the API .env and restart the worker.";
    }
    return `API error (${status ?? "network"}): ${error.message}`;
  }
  return `Unexpected error: ${error instanceof Error ? error.message : String(error)}`;
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------

/** Poll cadence, in ms. */
export const POLL_INTERVAL_MS = 2000;

/**
 * Overall polling bound. The shared `useInvocationStatusPoll` (onboarding)
 * polls until terminal with no cap, so a workflow that never terminates would
 * poll forever and strand the user. Cap the local poll at ~5 minutes of 2s
 * polls, mirroring `useWorkPatternImportInvocation`'s `MAX_POLL_ATTEMPTS`
 * (see `src/work-patterns/hooks/WorkPatternImport/useWorkPatternImportInvocation.ts`);
 * once the cap is hit we stop re-fetching and surface a timeout message.
 */
export const MAX_POLL_ATTEMPTS = 150;

/** Consecutive transient status-request failures tolerated before stopping. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 5;

export const POLL_TIMEOUT_MESSAGE =
  "Validation is taking longer than expected. Refresh to check its status, or try again.";

export const POLL_FAILURE_MESSAGE =
  "Unable to check the import status after repeated network errors. Check your connection and try again.";

export const MISSING_FILE_IDENTIFIER_MESSAGE =
  "No file identifier found. Please upload a file before importing.";

const POLL_QUERY_KEY_ROOT = "splitCostingImportInvocation";

function pollQueryKey(
  agentId: string | null | undefined,
  workflowId: string | null | undefined
) {
  return [POLL_QUERY_KEY_ROOT, agentId, workflowId] as const;
}

function isTransientPollError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  return !error.response || error.response.status >= 500;
}

function isTerminalPollRequestFailure(
  error: unknown,
  failureCount: number
): boolean {
  if (!error) return false;
  return (
    !isTransientPollError(error) ||
    failureCount >= MAX_CONSECUTIVE_POLL_FAILURES
  );
}

function classifyPollRequestError(error: unknown): string {
  if (isTransientPollError(error)) return POLL_FAILURE_MESSAGE;
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    return `Unable to check the import status${
      status ? ` (HTTP ${status})` : ""
    }. Please try again.`;
  }
  return `Unable to check the import status: ${
    error instanceof Error ? error.message : String(error)
  }`;
}

function useSplitCostingInvocationPoll(
  agentId: string | null | undefined,
  workflowId: string | null | undefined
) {
  const pollIdentity = `${agentId ?? ""}:${workflowId ?? ""}`;
  const consecutiveFailuresRef = useRef({
    pollIdentity,
    count: 0,
  });
  if (consecutiveFailuresRef.current.pollIdentity !== pollIdentity) {
    consecutiveFailuresRef.current = { pollIdentity, count: 0 };
  }

  const query = useQuery({
    queryKey: pollQueryKey(agentId, workflowId),
    queryFn: async () => {
      try {
        const result = await api.getInvocationStatus(
          agentId as string,
          workflowId as string
        );
        consecutiveFailuresRef.current.count = 0;
        return result;
      } catch (error) {
        consecutiveFailuresRef.current.count += 1;
        throw error;
      }
    },
    enabled: !!agentId && !!workflowId,
    refetchInterval: (query) => {
      if (isTerminalInvocationStatus(query.state.data?.status)) return false;
      // Stop once the attempt cap is reached — a non-terminating workflow
      // must not be polled indefinitely.
      if (query.state.dataUpdateCount >= MAX_POLL_ATTEMPTS) return false;
      if (
        isTerminalPollRequestFailure(
          query.state.error,
          consecutiveFailuresRef.current.count
        )
      ) {
        return false;
      }
      return POLL_INTERVAL_MS * Math.max(1, consecutiveFailuresRef.current.count);
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  return {
    query,
    consecutiveFailureCount: consecutiveFailuresRef.current.count,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Orchestrates the validate phase of the split costing import wizard via the
 * agent-invocations surface (`POST /api/v1/agents/{id}/invocations`, polled
 * via `GET .../invocations/{workflow_id}`).
 *
 * Cloned from `useWorkPatternImportInvocation`'s shape (see
 * `src/work-patterns/hooks/WorkPatternImport/useWorkPatternImportInvocation.ts`)
 * per `.plans/split-costing-import-validate-web.md`. `commit()` follows the
 * same clone target's two-phase validate/commit pattern — see
 * `.plans/split-costing-import-commit-web.md` (step 6, Web half).
 *
 * `rows` (and the bound company context) are held in both React state — for
 * rendering — and a ref, mirroring the work pattern hook's documented fix for
 * a React batching race: a caller that binds new data and immediately calls
 * `validate()` (or `commit()`) in the same handler would otherwise read stale
 * state. Reading from the refs inside `validate()` / `commit()` avoids that.
 */
export function useSplitCostingImportInvocation(): SplitCostingImportManagerReturn {
  const agentConfig = useCompanyProvisionAgentId();
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<SplitCostingRow[]>([]);
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [mypayEnvironment, setMypayEnvironment] =
    useState<MyPayEnvironment | null>(null);
  const [fileIdentifier, setFileIdentifier] = useState<string | null>(null);
  const [guardError, setGuardError] = useState<string | null>(null);
  const [validationStale, setValidationStale] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [validationInputRevision, setValidationInputRevision] = useState(0);
  const [submittedValidationRevision, setSubmittedValidationRevision] =
    useState<number | null>(null);
  const [validationDisplaySnapshot, setValidationDisplaySnapshot] =
    useState<SplitCostingImportWorkflowResult | null>(null);

  /** Synchronous ref mirrors — prevent React state-batching races (see doc comment above). */
  const rowsRef = useRef<SplitCostingRow[]>([]);
  const companyCodeRef = useRef<string | null>(null);
  const mypayEnvironmentRef = useRef<MyPayEnvironment | null>(null);
  const fileIdentifierRef = useRef<string | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);
  const validationStaleRef = useRef(false);
  const validationInputRevisionRef = useRef(0);
  const validationSubmissionSequenceRef = useRef(0);
  const validateWorkflowRevisionRef = useRef<number | null>(null);
  const commitSubmissionSequenceRef = useRef(0);

  // Invocation identifiers for the validate poll — set once invoke succeeds.
  const [validateAgentId, setValidateAgentId] = useState<string | null>(null);
  const [validateWorkflowId, setValidateWorkflowId] = useState<
    string | null
  >(null);

  const {
    query: validatePoll,
    consecutiveFailureCount: validatePollFailureCount,
  } = useSplitCostingInvocationPoll(validateAgentId, validateWorkflowId);

  // The number of resolved polls (`dataUpdateCount`) lives on the query
  // *state*, not the `useQuery` result object. Read `dataUpdatedAt` (which
  // changes on every poll — `data` is referentially stable across identical
  // statuses via structural sharing) so each poll re-renders the hook, then
  // read the authoritative attempt count from the query cache.
  const validateAttempts = validatePoll.dataUpdatedAt
    ? (queryClient.getQueryState(
        pollQueryKey(validateAgentId, validateWorkflowId)
      )?.dataUpdateCount ?? 0)
    : 0;

  // A poll has "timed out" once it hit the attempt cap while still non-terminal.
  const validateTimedOut =
    !!validateWorkflowId &&
    !isTerminalInvocationStatus(validatePoll.data?.status) &&
    validateAttempts >= MAX_POLL_ATTEMPTS;
  const validatePollRequestFailed = isTerminalPollRequestFailure(
    validatePoll.error,
    validatePollFailureCount
  );

  // Derived straight from the poll data during render (mirrors
  // `useWorkPatternImportInvocation`) — no render-lag, no mirrored state to
  // reset manually on validate()/clear(). Covers both "COMPLETED with zero
  // errors" and "COMPLETED with errors" — the caller reads `errors.length`.
  const validationReport = useMemo<SplitCostingImportWorkflowResult | null>(
    () => {
      if (
        validatePoll.data?.status !== InvocationStatusEnum.COMPLETED ||
        validateWorkflowRevisionRef.current !==
          validationInputRevisionRef.current
      ) {
        return null;
      }
      const result = validatePoll.data.result;
      return isSplitCostingImportResult(result)
        ? mergeClientValidationErrors(result, rowsRef.current)
        : null;
    },
    [validatePoll.data]
  );
  const validationDisplayReport =
    validationReport ?? validationDisplaySnapshot;

  useEffect(() => {
    if (validationReport !== null) {
      setValidationDisplaySnapshot(validationReport);
    }
  }, [validationReport]);

  // Clear the stale-validation flag once a fresh validate() reaches
  // COMPLETED — mirrors `useWorkPatternImportInvocation`'s equivalent effect.
  useEffect(() => {
    if (
      validatePoll.data?.status === InvocationStatusEnum.COMPLETED &&
      validateWorkflowRevisionRef.current ===
        validationInputRevisionRef.current
    ) {
      validationStaleRef.current = false;
      setValidationStale(false);
    }
  }, [validatePoll.data?.status, validatePoll.dataUpdatedAt]);

  // Error surfaced from a terminal poll status (FAILED/TERMINATED) or a
  // COMPLETED status whose payload didn't match the expected result shape.
  const pollError = useMemo<string | null>(() => {
    const status = validatePoll.data?.status;
    if (
      status === InvocationStatusEnum.COMPLETED &&
      !isSplitCostingImportResult(validatePoll.data?.result)
    ) {
      return "Unexpected response from validation workflow. Please try again.";
    }
    if (
      status === InvocationStatusEnum.FAILED ||
      status === InvocationStatusEnum.TERMINATED
    ) {
      return (
        validatePoll.data?.error ??
        "Validation workflow failed. Please try again."
      );
    }
    return null;
  }, [validatePoll.data]);

  const validateMutation = useMutation({
    mutationFn: (args: {
      agentId: string;
      serviceAccountId: string;
      companyCode: string;
      rows: SplitCostingContractRow[];
      idempotencyKey: string;
      mypayEnvironment: MyPayEnvironment;
      fileIdentifier: string;
      validationInputRevision: number;
      validationSubmissionSequence: number;
    }) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "split_costing_import",
          mode: "validate",
          company_code: args.companyCode,
          rows: args.rows,
          idempotency_key: args.idempotencyKey,
          mypay_environment: args.mypayEnvironment,
          file_identifier: args.fileIdentifier,
        } as GenericPayload,
      }),
    onSuccess: (data, variables) => {
      if (
        variables.validationInputRevision !==
          validationInputRevisionRef.current ||
        variables.validationSubmissionSequence !==
          validationSubmissionSequenceRef.current
      ) {
        return;
      }
      validateWorkflowRevisionRef.current =
        variables.validationInputRevision;
      setValidateAgentId(variables.agentId);
      setValidateWorkflowId(data.workflow_id);
    },
    // Invoke failures are read off `validateMutation.error` and folded into
    // `managerError` below — no onError setState needed.
  });

  // Invocation identifiers for the commit poll — set once commit's invoke
  // succeeds. Kept entirely separate from the validate poll's state/query key
  // so a commit in flight can never be mistaken for (or clobber) the validate
  // result the confirm gate was computed from.
  const [commitAgentId, setCommitAgentId] = useState<string | null>(null);
  const [commitWorkflowId, setCommitWorkflowId] = useState<string | null>(
    null
  );

  const {
    query: commitPoll,
    consecutiveFailureCount: commitPollFailureCount,
  } = useSplitCostingInvocationPoll(commitAgentId, commitWorkflowId);

  const commitAttempts = commitPoll.dataUpdatedAt
    ? (queryClient.getQueryState(
        pollQueryKey(commitAgentId, commitWorkflowId)
      )?.dataUpdateCount ?? 0)
    : 0;

  const commitTimedOut =
    !!commitWorkflowId &&
    !isTerminalInvocationStatus(commitPoll.data?.status) &&
    commitAttempts >= MAX_POLL_ATTEMPTS;
  const commitPollRequestFailed = isTerminalPollRequestFailure(
    commitPoll.error,
    commitPollFailureCount
  );

  const commitResult = useMemo<SplitCostingImportWorkflowResult | null>(
    () => {
      if (commitPoll.data?.status !== InvocationStatusEnum.COMPLETED) {
        return null;
      }
      const result = commitPoll.data.result;
      return isSplitCostingImportResult(result) ? result : null;
    },
    [commitPoll.data]
  );

  const commitPollError = useMemo<string | null>(() => {
    const status = commitPoll.data?.status;
    if (
      status === InvocationStatusEnum.COMPLETED &&
      !isSplitCostingImportResult(commitPoll.data?.result)
    ) {
      return "Unexpected response from commit workflow. Please try again.";
    }
    if (
      status === InvocationStatusEnum.FAILED ||
      status === InvocationStatusEnum.TERMINATED
    ) {
      return (
        commitPoll.data?.error ?? "Commit workflow failed. Please try again."
      );
    }
    return null;
  }, [commitPoll.data]);

  const commitMutation = useMutation({
    mutationFn: (args: {
      agentId: string;
      serviceAccountId: string;
      companyCode: string;
      rows: SplitCostingContractRow[];
      idempotencyKey: string;
      mypayEnvironment: MyPayEnvironment;
      fileIdentifier: string;
      commitInputRevision: number;
      commitSubmissionSequence: number;
    }) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "split_costing_import",
          mode: "commit",
          company_code: args.companyCode,
          rows: args.rows,
          idempotency_key: args.idempotencyKey,
          mypay_environment: args.mypayEnvironment,
          file_identifier: args.fileIdentifier,
        } as GenericPayload,
      }),
    onSuccess: (data, variables) => {
      if (
        variables.commitInputRevision !==
          validationInputRevisionRef.current ||
        variables.commitSubmissionSequence !==
          commitSubmissionSequenceRef.current
      ) {
        return;
      }
      setCommitAgentId(variables.agentId);
      setCommitWorkflowId(data.workflow_id);
    },
    // Invoke failures are read off `commitMutation.error` and folded into
    // `managerError` below — no onError setState needed.
  });

  const setPendingRows = (
    nextRows: SplitCostingRow[],
    nextCompanyCode: string,
    nextMypayEnvironment: MyPayEnvironment,
    nextFileIdentifier: string | null = null
  ) => {
    // A re-upload (new rows) after a completed validation invalidates that
    // validation. The initial bind — and a rebind with the same rows
    // reference — must not mark a fresh state as stale.
    const inputsChanged =
      nextRows !== rowsRef.current ||
      nextCompanyCode !== companyCodeRef.current ||
      nextMypayEnvironment !== mypayEnvironmentRef.current ||
      nextFileIdentifier !== fileIdentifierRef.current;
    const invalidatesSubmittedValidation =
      inputsChanged && idempotencyKeyRef.current !== null;

    if (inputsChanged) {
      validationInputRevisionRef.current += 1;
      validateWorkflowRevisionRef.current = null;
      setValidationInputRevision(validationInputRevisionRef.current);
      setValidationDisplaySnapshot(null);
    }

    rowsRef.current = nextRows;
    companyCodeRef.current = nextCompanyCode;
    mypayEnvironmentRef.current = nextMypayEnvironment;
    fileIdentifierRef.current = nextFileIdentifier;
    setRows(nextRows);
    setCompanyCode(nextCompanyCode);
    setMypayEnvironment(nextMypayEnvironment);
    setFileIdentifier(nextFileIdentifier);
    setGuardError(null);
    if (inputsChanged) {
      validationStaleRef.current = invalidatesSubmittedValidation;
      setValidationStale(invalidatesSubmittedValidation);
    }
    setValidateAgentId(null);
    setValidateWorkflowId(null);
    validateMutation.reset();
  };

  const updateRow = (index: number, nextRow: SplitCostingRow) => {
    if (index < 0 || index >= rowsRef.current.length) return;

    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    validationStaleRef.current = true;
    const nextRows = rowsRef.current.map((row, rowIndex) =>
      rowIndex === index ? nextRow : row
    );
    rowsRef.current = nextRows;
    idempotencyKeyRef.current = null;

    setRows(nextRows);
    setValidationDisplaySnapshot((current) => {
      const source = validationReport ?? current;
      return source === null
        ? null
        : omitValidationErrorsForRow(source, index);
    });
    setValidationInputRevision(validationInputRevisionRef.current);
    setSubmittedValidationRevision(null);
    setValidationStale(true);
    setIdempotencyKey(null);
    setGuardError(null);

    setValidateAgentId(null);
    setValidateWorkflowId(null);
    validateMutation.reset();
    setCommitAgentId(null);
    setCommitWorkflowId(null);
    commitMutation.reset();
  };

  const validate = async (): Promise<void> => {
    const batchRows = rowsRef.current;
    const batchCompanyCode = companyCodeRef.current;
    const batchMypayEnvironment = mypayEnvironmentRef.current;

    if (batchRows.length === 0) return;

    if (!batchCompanyCode) {
      setGuardError(
        "No company context found. Please validate a company before importing."
      );
      return;
    }

    if (!batchMypayEnvironment) {
      setGuardError(
        "No Datapay environment found. Please validate a company before importing."
      );
      return;
    }

    if (!agentConfig.isConfigured) {
      setGuardError(
        "Split costing import is not available in this environment."
      );
      return;
    }

    const batchFileIdentifier = fileIdentifierRef.current?.trim();
    if (!batchFileIdentifier) {
      setGuardError(MISSING_FILE_IDENTIFIER_MESSAGE);
      return;
    }

    // Mint a fresh idempotency key — reused by commit().
    const nextIdempotencyKey = crypto.randomUUID();
    idempotencyKeyRef.current = nextIdempotencyKey;
    setIdempotencyKey(nextIdempotencyKey);
    const inputRevision = validationInputRevisionRef.current;
    const submissionSequence = ++validationSubmissionSequenceRef.current;
    setSubmittedValidationRevision(inputRevision);

    setGuardError(null);
    // Reset the poll so a stale result/timeout from a prior run doesn't flash.
    setValidateAgentId(null);
    setValidateWorkflowId(null);

    validateMutation.mutate({
      agentId: agentConfig.agentId!,
      serviceAccountId: agentConfig.serviceAccountId!,
      companyCode: batchCompanyCode,
      rows: batchRows.map(toContractRow),
      idempotencyKey: nextIdempotencyKey,
      mypayEnvironment: batchMypayEnvironment,
      fileIdentifier: batchFileIdentifier,
      validationInputRevision: inputRevision,
      validationSubmissionSequence: submissionSequence,
    });
  };

  /**
   * See `SplitCostingImportManagerReturn.commit` for the guard conditions.
   * `warningsAcknowledged` is supplied by the caller (the validate screen) —
   * this hook has no visibility into the warning-checkbox UI state.
   */
  const commit = async (warningsAcknowledged: boolean): Promise<void> => {
    // Back-nav + re-click while the result-screen poll is still running must
    // not fire a second invoke (same idempotency key, poll state reset).
    if (
      commitMutation.isPending ||
      (!!commitWorkflowId &&
        !isTerminalInvocationStatus(commitPoll.data?.status) &&
        !commitTimedOut)
    ) {
      return;
    }

    const batchRows = rowsRef.current;
    const batchCompanyCode = companyCodeRef.current;
    const batchMypayEnvironment = mypayEnvironmentRef.current;
    const batchFileIdentifier = fileIdentifierRef.current?.trim();
    const key = idempotencyKeyRef.current;

    if (
      key === null ||
      validationStaleRef.current ||
      validateWorkflowRevisionRef.current !==
        validationInputRevisionRef.current ||
      !isSplitCostingValidationCommitReady(
        validationReport,
        batchRows.length
      ) ||
      !warningsAcknowledged
    ) {
      return;
    }

    if (!batchCompanyCode || !batchMypayEnvironment) {
      setGuardError(
        "No company context found. Please validate a company before importing."
      );
      return;
    }

    if (!agentConfig.isConfigured) {
      setGuardError(
        "Split costing import is not available in this environment."
      );
      return;
    }

    if (!batchFileIdentifier) {
      setGuardError(MISSING_FILE_IDENTIFIER_MESSAGE);
      return;
    }

    setGuardError(null);
    // Reset the poll so a stale result/timeout from a prior attempt doesn't flash.
    setCommitAgentId(null);
    setCommitWorkflowId(null);
    const commitInputRevision = validationInputRevisionRef.current;
    const commitSubmissionSequence = ++commitSubmissionSequenceRef.current;

    commitMutation.mutate({
      agentId: agentConfig.agentId!,
      serviceAccountId: agentConfig.serviceAccountId!,
      companyCode: batchCompanyCode,
      rows: batchRows.map(toContractRow),
      idempotencyKey: key,
      mypayEnvironment: batchMypayEnvironment,
      fileIdentifier: batchFileIdentifier,
      commitInputRevision,
      commitSubmissionSequence,
    });
  };

  const clear = () => {
    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    rowsRef.current = [];
    companyCodeRef.current = null;
    mypayEnvironmentRef.current = null;
    fileIdentifierRef.current = null;
    idempotencyKeyRef.current = null;
    validationStaleRef.current = false;
    setRows([]);
    setCompanyCode(null);
    setMypayEnvironment(null);
    setFileIdentifier(null);
    setGuardError(null);
    setValidationStale(false);
    setIdempotencyKey(null);
    setValidationInputRevision(validationInputRevisionRef.current);
    setSubmittedValidationRevision(null);
    setValidationDisplaySnapshot(null);
    setValidateAgentId(null);
    setValidateWorkflowId(null);
    validateMutation.reset();
    setCommitAgentId(null);
    setCommitWorkflowId(null);
    commitMutation.reset();
  };

  // Coherent precedence: an imperative guard first, then an invoke failure
  // classified by status code (validate, then commit), then a poll timeout,
  // terminal poll-request failure, and terminal workflow-status error
  // (validate before commit within each category).
  const managerError =
    guardError ??
    (validateMutation.error
      ? classifyInvocationError(validateMutation.error)
      : null) ??
    (commitMutation.error
      ? classifyInvocationError(commitMutation.error)
      : null) ??
    (validateTimedOut ? POLL_TIMEOUT_MESSAGE : null) ??
    (commitTimedOut ? POLL_TIMEOUT_MESSAGE : null) ??
    (validatePollRequestFailed
      ? classifyPollRequestError(validatePoll.error)
      : null) ??
    (commitPollRequestFailed
      ? classifyPollRequestError(commitPoll.error)
      : null) ??
    pollError ??
    commitPollError;

  const isValidating =
    validateMutation.isPending ||
    (!!validateWorkflowId &&
      !isTerminalInvocationStatus(validatePoll.data?.status) &&
      !validateTimedOut &&
      !validatePollRequestFailed);

  const isCommitting =
    commitMutation.isPending ||
    (!!commitWorkflowId &&
      !isTerminalInvocationStatus(commitPoll.data?.status) &&
      !commitTimedOut &&
      !commitPollRequestFailed);
  const hasUncommittedImportProgress =
    rows.length > 0 &&
    !isSplitCostingCommitSuccessful(commitResult, rows.length);

  return {
    rows,
    companyCode,
    mypayEnvironment,
    fileIdentifier,
    validationReport,
    validationDisplayReport,
    validationStale,
    managerError,
    isValidating,
    isConfigLoading: agentConfig.isLoading,
    hasRows: rows.length > 0,
    hasUncommittedImportProgress,
    validationInputRevision,
    submittedValidationRevision,
    idempotencyKey,
    commitResult,
    isCommitting,
    setPendingRows,
    updateRow,
    validate,
    commit,
    clear,
  };
}
